<template>
  <div :class="['d-flex justify-space-between w-100', { 'text-wrapper': isMobile }]">
    <div class="d-flex flex-column w-100">
      <div class="d-flex justify-space-between">
        <c-typography data-testid="get-started-dashboard-title" variant="subtitle-1-600">Get started here </c-typography>
        <div v-if="showArrows">
          <c-button
            data-testid="home_go_prev"
            height="24"
            icon="ph:arrow-left"
            icon-color="rgb(var(--v-theme-copy-secondary))"
            icon-height="24"
            plain
            width="24"
            @click="goPrev"
          ></c-button>
          <c-button
            class="ml-5"
            data-testid="home_go_next"
            height="24"
            icon="ph:arrow-right"
            icon-color="rgb(var(--v-theme-copy-secondary))"
            icon-height="24"
            plain
            width="24"
            @click="goNext"
          ></c-button>
        </div>
      </div>
      <c-typography
        class="mt-1"
        color-class="copy-secondary"
        variant="body-2-400"
      >Try now our AI content creators
      </c-typography>
    </div>
  </div>
  <div :class="['d-flex flex-wrap pt-3', { 'title--mobile': isMobile }]">
    <v-slide-group ref="slideGroup" :show-arrows="false">
      <v-slide-group-item
        v-for="titleFeature in features.filter((el) => !el.hidden)"
        :key="titleFeature.name"
      >
        <home-title-card
          :feature="titleFeature"
          @open-modal="openInfoModal(titleFeature)"
        />
      </v-slide-group-item>
    </v-slide-group>
  </div>

  <tutorial-dialog
    v-model="infoModal"
    :description="dialogData.description"
    :first-picture="dialogData.firstPicture"
    :is-photo-studio="isPhotoTwinStudio"
    :second-picture="dialogData.secondPicture"
    :title="dialogData.title"
  />

</template>

<script lang="ts" setup>
  import HomeTitleCard from "./TitleCard.vue";
  import TutorialDialog from "../Dialogs/TutorialDialog.vue";

  import { isMobile } from "@/core/utils/mobile";
  import { computed, type PropType, ref } from "vue";

  import type { TitleFeature } from "@/core/types/other.types";

  //@ts-ignore
  import { CButton, CTypography } from "@charactr/wooper-ui/atoms";

  import { useMutationObserver } from "@vueuse/core";

  const props = defineProps({
    features: {
      type: Array as PropType<Array<TitleFeature>>,
      default: () => [],
    },
  });

  const slideGroup = ref();
  const showSliderBtns = ref(false);
  const infoModal = ref(false);
  const currentTutorial = ref("photoTwinStudio");

  const isPhotoTwinStudio = computed(() => {
    return currentTutorial.value === "photoTwinStudio";
  });

  const isVideoTranslation = computed(() => {
    return currentTutorial.value === "videoTranslation";
  });

  const showArrows = computed(() => isMobile.value || showSliderBtns.value);

  const dialogData = computed(() => {

    if (isVideoTranslation.value) {
      return {
        title: "Translate your videos",
        description: {
          title: "AI Video Translator",
          text: "Translate your videos into various languages with seamless lip-synced experience.",
          tags: ["video to video", "ai translation"],
          path: "/images/tutorial/videoTranslation_platform.png",
        },
        firstPicture: {
          path: "/images/tutorial/videoTranslation1.png",
          text: "Start by uploading your video...",
        },
        secondPicture: {
          path: "/images/tutorial/videoTranslation2.png",
          text: "Reach new audiences by using multi-language videos in your company's materials!",
        },
      };
    } else if (isPhotoTwinStudio.value) {
      return {
        title: "Create your dream self",
        description: {
          title: "Photo Twin Studio",
          text: "Describe your role or explore the ready-to-use prompt library prepared by us and generate yourself in many ways.",
          tags: ["photo to photo", "text to photo"],
          path: "/images/tutorial/phototwin_platform.png",
        },
        firstPicture: {
          path: "/images/tutorial/phototwin1.png",
          text: "Start by uploading your photos...",
        },
        secondPicture: {
          path: "/images/tutorial/phototwin2.png",
          text: "See yourself as a superhero, a medieval warrior, a pirate ship captain and many others!",
        },
      };
    } else {
      return {
        title: "Generate your AI Twin",
        description: {
          title: "Video Twin Studio",
          text: "Explore the comprehensive studio for creating your AI generated Twin. Choose a way to create content!",
          tags: ["text to video"],
          path: "/images/tutorial/videotwin_platform.png",
        },
        firstPicture: {
          path: "/images/tutorial/videotwin1.png",
          text: "Start by uploading your video...",
        },
        secondPicture: {
          path: "/images/tutorial/videotwin2.png",
          text: "Use your personalized AI Twin for business purposes or just for fun!",
        },
      };
    }
  });

  const goNext = () => {
    slideGroup.value.scrollTo("last");
  };

  const goPrev = () => {
    slideGroup.value.scrollTo("prev");
  };

  const openInfoModal = (feature: TitleFeature) => {
    currentTutorial.value = feature.name;
    infoModal.value = true;
  };

  useMutationObserver(
    slideGroup,
    (mutations) => {
      if (mutations[0]) {
        const isClassMutation = mutations[0].attributeName === "class";

        if (isClassMutation) {
          const classList = (mutations[0].target as HTMLElement).classList;

          showSliderBtns.value = Array.from(classList).includes(
            "v-slide-group--is-overflowing"
          );

          //in case when screen was resized and scroll buttons dissaperared, then scroll to first element
          if (!showSliderBtns.value) {
            slideGroup.value.scrollTo("first");
          }
        }
      }
    },
    {
      attributes: true,
    }
  );
</script>

<style lang="scss" scoped>
:deep(.v-slide-group__content) {
  gap: 16px;
  margin: 2px;
}
</style>
